import type {
  CasePage,
  FileTreeNode,
  ImportFileRow,
  ImportViewerFile,
  JobLogRow,
  JobRow,
  QuestionAnswer,
  QuestionAnswerDraft,
  QuestionRow
} from "@/features/app/types";

const IMAGE_FILE_EXTENSIONS = new Set<string>(["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"]);
const BRAND_STATUS_BASE_CLASS =
  "inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold tracking-[0.01em]";

const brandStatusToneClassNames = {
  neutral:
    `${BRAND_STATUS_BASE_CLASS} border-black/10 bg-black/[0.04] text-black/70 dark:border-white/12 dark:bg-white/[0.08] dark:text-white/72`,
  info:
    `${BRAND_STATUS_BASE_CLASS} border-[#C5DBDF] bg-[#DEEBED] text-[#24424A] dark:border-[#C5DBDF]/25 dark:bg-[#DEEBED]/10 dark:text-[#DEEBED]`,
  success:
    `${BRAND_STATUS_BASE_CLASS} border-[#22C153]/25 bg-[#22C153]/12 text-[#0F6B2C] dark:border-[#22C153]/30 dark:bg-[#22C153]/16 dark:text-[#9EF0B8]`,
  warning:
    `${BRAND_STATUS_BASE_CLASS} border-[#FDA64B]/35 bg-[#FDA64B]/16 text-[#8A4B00] dark:border-[#FDA64B]/35 dark:bg-[#FDA64B]/14 dark:text-[#FFD5AB]`,
  danger:
    `${BRAND_STATUS_BASE_CLASS} border-black/85 bg-black text-white dark:border-white/16 dark:bg-white/[0.12] dark:text-white`
} as const;

const caseStateLabels: Record<string, string> = {
  importing: "Importing",
  import_failed: "Import Failed",
  context_ready: "Ready for Questions",
  questions_generated: "Questions Ready",
  plan_generated: "Outline Ready",
  plan_approved: "Outline Approved",
  proposal_generated: "Proposal in Progress",
  proposal_document_generated: "Proposal Ready"
};

const caseSubStateLabels: Record<string, string> = {
  awaiting_answers: "Awaiting Answers",
  needs_review: "Needs Review",
  needs_regeneration: "Needs Refresh"
};

const jobStatusLabels: Record<string, string> = {
  queued: "Queued",
  running: "Running",
  succeeded: "Complete",
  failed: "Failed",
  cancelled: "Cancelled"
};

const planStatusLabels: Record<string, string> = {
  draft: "Draft",
  approved: "Approved",
  superseded: "Replaced"
};

const importStatusLabels: Record<ImportFileRow["status"], string> = {
  converted: "Converted",
  skipped: "Skipped",
  failed: "Failed"
};

export const isDarkBySystemPreference = (): boolean =>
  globalThis.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;

export const applyThemeToDom = (theme: "light" | "dark" | "system"): void => {
  const shouldUseDark = theme === "dark" || (theme === "system" && isDarkBySystemPreference());
  document.documentElement.classList.toggle("dark", shouldUseDark);
};

export const formatTimestamp = (value: string): string =>
  new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

export const formatCompactTimestamp = (value: string): string =>
  new Date(value).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });

export const formatDuration = (start: string, end: string, status: string): string => {
  const startMs = new Date(start).getTime();
  const endMs = status === "running" ? Date.now() : new Date(end).getTime();
  const seconds = Math.max(0, Math.round((endMs - startMs) / 1000));

  if (seconds < 60) {
    return `${seconds}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}m ${remainder}s`;
};

export const clampProgress = (value: number): number => Math.max(0, Math.min(100, value));
export const isActiveJobStatus = (status: string): boolean => status === "running" || status === "queued";

export const getCaseStateLabel = (state: string): string => caseStateLabels[state] ?? state.replaceAll("_", " ");

export const getCaseSubStateLabel = (subState: string | null): string | null =>
  subState ? caseSubStateLabels[subState] ?? subState.replaceAll("_", " ") : null;

export const getCaseStateBadgeClassName = (state: string): string => {
  if (state === "import_failed") {
    return brandStatusToneClassNames.danger;
  }

  if (state === "importing" || state === "context_ready" || state === "proposal_generated") {
    return brandStatusToneClassNames.info;
  }

  if (state === "questions_generated" || state === "plan_generated") {
    return brandStatusToneClassNames.warning;
  }

  if (state === "plan_approved" || state === "proposal_document_generated") {
    return brandStatusToneClassNames.success;
  }

  return brandStatusToneClassNames.neutral;
};

export const getJobStatusLabel = (status: string): string => jobStatusLabels[status] ?? status.replaceAll("_", " ");

export const getJobStatusBadgeClassName = (status: string): string => {
  if (status === "failed") {
    return brandStatusToneClassNames.danger;
  }

  if (status === "running" || status === "queued") {
    return status === "running" ? brandStatusToneClassNames.info : brandStatusToneClassNames.warning;
  }

  if (status === "succeeded") {
    return brandStatusToneClassNames.success;
  }

  return brandStatusToneClassNames.neutral;
};

export const getPlanStatusLabel = (status: string): string => planStatusLabels[status] ?? status.replaceAll("_", " ");

export const getPlanStatusBadgeClassName = (status: string): string => {
  if (status === "approved") {
    return brandStatusToneClassNames.success;
  }

  if (status === "draft") {
    return brandStatusToneClassNames.warning;
  }

  return brandStatusToneClassNames.neutral;
};

export const getDefaultExpandedActivityJobId = (jobs: JobRow[]): string | null =>
  jobs.find((job) => isActiveJobStatus(job.status))?.id ?? jobs[0]?.id ?? null;

export const groupJobLogsByJobId = (
  logs: JobLogRow[]
): {
  logsByJobId: Map<string, JobLogRow[]>;
  ungroupedLogs: JobLogRow[];
} => {
  const logsByJobId = new Map<string, JobLogRow[]>();
  const ungroupedLogs: JobLogRow[] = [];

  for (const log of logs) {
    if (!log.job_id) {
      ungroupedLogs.push(log);
      continue;
    }

    const existingLogs = logsByJobId.get(log.job_id) ?? [];
    existingLogs.push(log);
    logsByJobId.set(log.job_id, existingLogs);
  }

  return { logsByJobId, ungroupedLogs };
};

export const normalizeImportPath = (value: string): string =>
  value
    .replaceAll("\\", "/")
    .replace(/^\/+/, "")
    .replaceAll(/\/+/g, "/");

export const extensionFromPath = (value: string): string => {
  const dotIndex = value.lastIndexOf(".");
  return dotIndex >= 0 ? value.slice(dotIndex + 1).toLowerCase() : "";
};

export const isMarkdownPath = (value: string): boolean => {
  const ext = extensionFromPath(value);
  return ext === "md" || ext === "markdown";
};

export const isImagePath = (value: string): boolean => IMAGE_FILE_EXTENSIONS.has(extensionFromPath(value));

export const buildImportViewerFiles = (importFiles: ImportFileRow[]): ImportViewerFile[] => {
  const deduplicated = new Map<string, ImportViewerFile>();

  for (const file of importFiles) {
    const rawPath = normalizeImportPath(`raw/${file.relative_path}`);
    if (rawPath) {
      deduplicated.set(rawPath, { path: rawPath });
    }

    if (file.output_relative_path) {
      const outputPath = normalizeImportPath(file.output_relative_path);
      if (outputPath) {
        deduplicated.set(outputPath, { path: outputPath });
      }
    }
  }

  return Array.from(deduplicated.values()).sort((left, right) => left.path.localeCompare(right.path));
};

export const buildFileTree = (files: ImportViewerFile[]): FileTreeNode[] => {
  type MutableNode = {
    name: string;
    path: string | null;
    children: Map<string, MutableNode>;
  };

  const root: MutableNode = { name: "", path: null, children: new Map() };

  for (const file of files) {
    const parts = file.path.split("/").filter(Boolean);
    let cursor = root;
    let currentPath = "";

    for (const [index, part] of parts.entries()) {
      currentPath = currentPath ? `${currentPath}/${part}` : part;

      if (!cursor.children.has(part)) {
        cursor.children.set(part, { name: part, path: null, children: new Map() });
      }

      const nextNode = cursor.children.get(part);
      if (!nextNode) {
        continue;
      }

      if (index === parts.length - 1) {
        nextNode.path = currentPath;
      }

      cursor = nextNode;
    }
  }

  const toTreeNodes = (children: Map<string, MutableNode>): FileTreeNode[] =>
    Array.from(children.values())
      .sort((left, right) => {
        const leftIsDirectory = left.path === null;
        const rightIsDirectory = right.path === null;

        if (leftIsDirectory !== rightIsDirectory) {
          return leftIsDirectory ? -1 : 1;
        }

        return left.name.localeCompare(right.name);
      })
      .map((node) => ({
        name: node.name,
        path: node.path,
        children: toTreeNodes(node.children)
      }));

  return toTreeNodes(root.children);
};

export const importStatusBadgeClass = (status: ImportFileRow["status"]): string => {
  if (status === "converted") {
    return brandStatusToneClassNames.success;
  }

  if (status === "skipped") {
    return brandStatusToneClassNames.warning;
  }

  return brandStatusToneClassNames.danger;
};

export const getImportStatusLabel = (status: ImportFileRow["status"]): string => importStatusLabels[status];

export const parseJsonArrayString = (value: string): string[] => {
  try {
    const parsed = JSON.parse(value) as string[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return value
      .split("\n")
      .map((item) => item.trim().replace(/^[-*]\s+/, ""))
      .filter((item) => item.length > 0);
  }
};

export const CUSTOM_QUESTION_OPTION = "__custom__";

export const buildQuestionAnswerDraft = (question: QuestionRow): QuestionAnswerDraft => {
  const selectedOption = question.answer?.selectedOption?.trim();
  const freeText = question.answer?.freeText ?? "";

  if (selectedOption) {
    return {
      selectedOption,
      freeText
    };
  }

  if (freeText.trim()) {
    return {
      selectedOption: CUSTOM_QUESTION_OPTION,
      freeText
    };
  }

  return {
    selectedOption: question.suggestedOption ?? "",
    freeText: ""
  };
};

export const hasQuestionAnswerValue = (
  selectedOption: string | null | undefined,
  freeText: string | null | undefined
): boolean => {
  const normalizedOption = selectedOption?.trim();
  return Boolean(
    (normalizedOption && normalizedOption !== CUSTOM_QUESTION_OPTION) ||
    freeText?.trim()
  );
};

export const hasSavedQuestionAnswer = (answer: QuestionAnswer | null | undefined): boolean =>
  hasQuestionAnswerValue(answer?.selectedOption, answer?.freeText);

export const toQuestionAnswerPayload = (
  draft: QuestionAnswerDraft
): {
  selectedOption: string | null;
  freeText: string | null;
} => {
  const freeText = draft.freeText.trim() || null;

  if (draft.selectedOption === CUSTOM_QUESTION_OPTION) {
    return {
      selectedOption: null,
      freeText
    };
  }

  return {
    selectedOption: draft.selectedOption.trim() || null,
    freeText
  };
};

export const getCasePageLabel = (page: CasePage): string => {
  if (page === "overview") return "Overview";
  if (page === "files") return "Tender Documents";
  if (page === "questions") return "Steering Questions";
  if (page === "plan") return "Outline";
  if (page === "activity") return "Chat";
  return "Proposal";
};
